<template>
    <div class="proofreading-panel">
        <div class="state-breadcrumb">
            <span class="state-label">{{ $t('proofreading.state') }}:</span>
            <span :class="{ bold: isActive }">{{ $t('proofreading.stateInTranslation') }}</span>
            <span class="separator"> > </span>
            <span :class="{ bold: isInProofreading }">{{ $t('proofreading.stateInProofreading') }}</span>
            <span class="separator"> > </span>
            <span :class="{ bold: isProofreadingComplete }">{{ $t('proofreading.stateProofreadingComplete') }}</span>
        </div>

        <div v-if="isActive" class="actions">
            <el-button type="primary"
                       plain
                       :loading="loading"
                       @click="exportForReview">
                {{ $t('proofreading.exportForProofreading') }}
            </el-button>
        </div>

        <div v-else-if="isInProofreading">
            <div class="info-row">
                <span class="info-label">{{ $t('proofreading.exportedAt') }}:</span>
                <span>{{ formatDate(document.proofreadingExportedAt) }}</span>
            </div>
            <div v-if="document.proofreadingExportVersion" class="info-row">
                <span class="info-label">{{ $t('proofreading.exportVersion') }}:</span>
                <span>{{ formatVersion(document.proofreadingExportVersion) }}</span>
            </div>
            <div class="info-row">
                <span class="info-label">{{ $t('proofreading.documentPassword') }}:</span>
                <span class="password">{{ document.proofreadingPassword }}</span>
                <el-tooltip :content="$t('proofreading.copyPassword')">
                    <span class="material-symbols-outlined copy-button" @click="copyPassword">
                        content_copy
                    </span>
                </el-tooltip>
            </div>
            <div class="actions">
                <input ref="fileInput" type="file" accept=".docx" style="display:none" @change="onFileSelected" />
                <el-button type="primary"
                           plain
                           :loading="loading"
                           @click="triggerFileInput">
                    {{ $t('proofreading.uploadProofreadDocument') }}
                </el-button>
                <el-button type="warning"
                           :loading="loading"
                           @click="cancelProofreading">
                    {{ $t('proofreading.cancelProofreading') }}
                </el-button>
            </div>
        </div>

        <div v-else-if="isProofreadingComplete">
            <div class="info-row">
                <span class="info-label">{{ $t('proofreading.exportedAt') }}:</span>
                <span>{{ formatDate(document.proofreadingExportedAt) }}</span>
            </div>
            <div class="info-row">
                <span class="info-label">{{ $t('proofreading.completedAt') }}:</span>
                <span>{{ formatDate(document.proofreadingCompletedAt) }}</span>
            </div>
            <div class="actions">
                <el-button type="warning"
                           :loading="loading"
                           @click="resetToInTranslation">
                    {{ $t('proofreading.resetToInTranslation') }}
                </el-button>
            </div>
        </div>

    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { ElMessage, ElMessageBox } from 'element-plus';

    import { Document } from '@/models/Document';
    import { DocumentStatus } from '@/models/DocumentStatus';

    import { proofreadingApi, ProofreadingAnalysisResult } from '@/api/proofreadingApi';
    import { navigationService } from '@/services/navigationService';

    export default defineComponent({
        props: {
            document: {
                type: Object as PropType<Document>,
                required: true,
            },
        },
        emits: ['reload'],
        data() {
            return {
                loading: false,
            };
        },
        computed: {
            documentId(): number {
                return this.document.id;
            },
            isActive(): boolean {
                return this.document.status === DocumentStatus.Active;
            },
            isInProofreading(): boolean {
                return this.document.status === DocumentStatus.InProofreading;
            },
            isProofreadingComplete(): boolean {
                return this.document.status === DocumentStatus.ProofreadingComplete;
            },
        },
        methods: {
            async exportForReview() {
                try {
                    await ElMessageBox.confirm(
                        this.$t('proofreadingExport.exportConfirmMessage'),
                        this.$t('proofreadingExport.exportConfirmTitle'),
                        {
                            confirmButtonText: this.$t('proofreadingExport.exportConfirmButton'),
                            cancelButtonText: this.$t('proofreadingExport.exportConfirmCancel'),
                            type: 'info',
                        }
                    );
                } catch {
                    return;
                }
                this.loading = true;
                await proofreadingApi.exportForProofreading(this.documentId);
                this.loading = false;
                navigationService.goProofreadingDocumentPage(this.documentId);
            },
            async cancelProofreading() {
                try {
                    await ElMessageBox.confirm(
                        this.$t('proofreadingUploadCancel.cancelConfirmMessage', { version: this.formatVersion(this.document.proofreadingExportVersion) }),
                        this.$t('proofreadingUploadCancel.cancelConfirmTitle'),
                        {
                            confirmButtonText: this.$t('proofreadingUploadCancel.cancelConfirmButton'),
                            cancelButtonText: this.$t('proofreadingUploadCancel.cancelConfirmCancel'),
                            type: 'warning',
                        }
                    );
                } catch {
                    return;
                }
                this.loading = true;
                await proofreadingApi.cancelProofreading(this.documentId);
                this.loading = false;
                navigationService.goDocumentPage(this.documentId);
            },
            triggerFileInput() {
                (this.$refs.fileInput as HTMLInputElement).click();
            },
            async onFileSelected(event: Event) {
                const input = event.target as HTMLInputElement;
                const file = input.files?.[0];
                input.value = '';
                if (!file) return;

                this.loading = true;
                let result: ProofreadingAnalysisResult | null;
                try {
                    result = await proofreadingApi.analyzeImport(this.documentId, file);
                } finally {
                    this.loading = false;
                }

                if (!result) return;
                if (!result.isValid) {
                    if (result.duplicateGroups && result.duplicateGroups.length > 0) {
                        await this.confirmMergeAndApply(result, file);
                        return;
                    }
                    await this.showUploadError(result);
                    return;
                }
                await this.confirmAndApply(result, file);
            },
            async showUploadError(result: ProofreadingAnalysisResult): Promise<void> {
                if (result.isUnreadable) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadUnreadableError'),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.hasInvalidStructure) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadInvalidStructureError'),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.multilineSegmentNumbers.length > 0) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadMultilineTargetError', { segments: result.multilineSegmentNumbers.join(', ') }),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.isLocked) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadLockedError'),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.hasComments) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadHasCommentsError'),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.unreviewedSegmentNumbers.length > 0) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadUnreviewedError', { segments: result.unreviewedSegmentNumbers.join(', ') }),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.missingSegments > 0) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadMissingSegmentsError', { count: result.missingSegments }),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else if (result.addedSegments > 0) {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadAddedSegmentsError', { count: result.addedSegments }),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                } else {
                    await ElMessageBox.alert(
                        this.$t('proofreadingUploadCancel.uploadVersionMismatchError'),
                        this.$t('proofreadingUploadCancel.uploadErrorTitle'),
                        { type: 'error' }
                    );
                }
            },
            async confirmAndApply(result: ProofreadingAnalysisResult, file: File): Promise<void> {
                const hasTab = result.tabSegmentNumbers.length > 0;
                const hasIdmlStyles = result.idmlUnsupportedStyleSegmentNumbers.length > 0;
                const hasUnsupported = result.unsupportedFormattingSegmentNumbers.length > 0;
                const hasMissing = result.missingSegments > 0;

                let message: string;
                let title: string;
                let confirmButtonText: string;
                let type: 'warning' | 'info';
                let useHtml: boolean;

                if (hasTab) {
                    message = this.$t('proofreadingUploadCancel.uploadTabConfirmMessage', {
                        segments: result.tabSegmentNumbers.join(', '),
                    });
                    title = this.$t('proofreadingUploadCancel.uploadTabConfirmTitle');
                    confirmButtonText = this.$t('proofreadingUploadCancel.uploadTabConfirmButton');
                    type = 'warning';
                    useHtml = true;
                } else if (hasIdmlStyles) {
                    message = this.$t('proofreadingUploadCancel.uploadIdmlStylesConfirmMessage', {
                        segments: result.idmlUnsupportedStyleSegmentNumbers.join(', '),
                    });
                    title = this.$t('proofreadingUploadCancel.uploadIdmlStylesConfirmTitle');
                    confirmButtonText = this.$t('proofreadingUploadCancel.uploadIdmlStylesConfirmButton');
                    type = 'warning';
                    useHtml = true;
                } else if (hasUnsupported) {
                    message = this.$t('proofreadingUploadCancel.uploadUnsupportedFormattingConfirmMessage', {
                        segments: result.unsupportedFormattingSegmentNumbers.join(', '),
                    });
                    title = this.$t('proofreadingUploadCancel.uploadUnsupportedFormattingConfirmTitle');
                    confirmButtonText = this.$t('proofreadingUploadCancel.uploadUnsupportedFormattingConfirmButton');
                    type = 'warning';
                    useHtml = true;
                } else if (hasMissing) {
                    message = this.$t('proofreadingUploadCancel.uploadMissingConfirmMessage', {
                        missing: result.missingSegments,
                        total: result.totalSegments,
                        updated: result.updatedSegments,
                    });
                    title = this.$t('proofreadingUploadCancel.uploadMissingConfirmTitle');
                    confirmButtonText = this.$t('proofreadingUploadCancel.uploadMissingConfirmButton');
                    type = 'warning';
                    useHtml = true;
                } else {
                    message = this.$t('proofreadingUploadCancel.uploadConfirmMessage', {
                        updated: result.updatedSegments,
                        total: result.totalSegments,
                    });
                    title = this.$t('proofreadingUploadCancel.uploadConfirmTitle');
                    confirmButtonText = this.$t('proofreadingUploadCancel.uploadConfirmButton');
                    type = 'info';
                    useHtml = false;
                }
                try {
                    await ElMessageBox.confirm(
                        message,
                        title,
                        {
                            confirmButtonText,
                            cancelButtonText: this.$t('proofreadingUploadCancel.uploadConfirmCancel'),
                            type,
                            dangerouslyUseHTMLString: useHtml,
                        }
                    );
                } catch {
                    return;
                }

                this.loading = true;
                try {
                    const success = await proofreadingApi.completeProofreading(this.documentId, file);
                    if (success) {
                        navigationService.goDocumentPage(this.documentId);
                    }
                } finally {
                    this.loading = false;
                }
            },
            async confirmMergeAndApply(result: ProofreadingAnalysisResult, file: File): Promise<void> {
                const groups = result.duplicateGroups
                    .map(group => `• ${this.$t('proofreadingUploadCancel.uploadDuplicateRow')} ${this.joinRowNumbers(group)}`)
                    .join('<br>');
                const message = this.$t('proofreadingUploadCancel.uploadDuplicateConfirmMessage', { groups });
                try {
                    await ElMessageBox.confirm(
                        message,
                        this.$t('proofreadingUploadCancel.uploadDuplicateConfirmTitle'),
                        {
                            confirmButtonText: this.$t('proofreadingUploadCancel.uploadDuplicateMergeButton'),
                            cancelButtonText: this.$t('proofreadingUploadCancel.uploadDuplicateRejectButton'),
                            type: 'warning',
                            dangerouslyUseHTMLString: true,
                        }
                    );
                } catch {
                    return;
                }

                this.loading = true;
                try {
                    const success = await proofreadingApi.completeProofreading(this.documentId, file, true);
                    if (success) {
                        navigationService.goDocumentPage(this.documentId);
                    }
                } finally {
                    this.loading = false;
                }
            },
            joinRowNumbers(numbers: string[]): string {
                const tagged = numbers.map(n => `#${this.escapeHtml(n)}`);
                if (tagged.length <= 1) return tagged.join('');
                const and = this.$t('proofreadingUploadCancel.uploadDuplicateAnd');
                return `${tagged.slice(0, -1).join(', ')} ${and} ${tagged[tagged.length - 1]}`;
            },
            escapeHtml(value: string): string {
                const map: Record<string, string> = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;',
                };
                return value.replace(/[&<>"']/g, c => map[c]);
            },
            async resetToInTranslation() {
                try {
                    await ElMessageBox.confirm(
                        this.$t('proofreadingUploadCancel.resetConfirmMessage'),
                        this.$t('proofreadingUploadCancel.resetConfirmTitle'),
                        {
                            confirmButtonText: this.$t('proofreadingUploadCancel.resetConfirmButton'),
                            cancelButtonText: this.$t('proofreadingUploadCancel.resetConfirmCancel'),
                            type: 'warning',
                        }
                    );
                } catch {
                    return;
                }
                this.loading = true;
                await proofreadingApi.resetProofreadingComplete(this.documentId);
                this.loading = false;
                this.$emit('reload');
            },
            async copyPassword() {
                await navigator.clipboard.writeText(this.document.proofreadingPassword ?? '');
                ElMessage.success(this.$t('proofreading.passwordCopied'));
            },
            formatDate(value?: string): string {
                if (!value) return '';
                return new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
            },
            formatVersion(version: number | undefined): string {
                if (!version) return '';
                return `V-${String(version).padStart(3, '0')}`;
            },
        },
    });
</script>

<style lang="scss" scoped>
    .proofreading-panel {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .state-breadcrumb {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 14px;

            .state-label {
                font-weight: bold;
                margin-right: 4px;
            }

            .bold {
                font-weight: bold;
            }
        }

        .info-row {
            display: flex;
            gap: 8px;
            font-size: 14px;

            .info-label {
                font-weight: bold;
            }

            .password {
                font-family: monospace;
            }

            .copy-button {
                cursor: pointer;
                font-size: 18px;
                color: var(--el-color-primary);
            }
        }

        .actions {
            display: flex;
            gap: 10px;
            margin-top: 20px;
        }
    }
</style>
